/**
 * 端末とルーム（ゲームサーバーの ocgcore のルーム。docs/game-server.md・ocgcore/README.md「ルーム（room/）」）のつなぎ方の状態遷移表（Issue #765。索引 #761）。純粋。
 * Web（`components/ocgCore/_room.js` の WebSocket）と iPad（`Core/RoomLink.swift` の URLSessionWebSocketTask）は**殻**で、事象を渡して返った actions を実行するだけ。
 *
 * 約束（本番 #764 と）: 本番の `OcgSeatState` の返り（席なしの観戦も）に `room: {room_id, ws_url, key, seat}` が付いていれば、その試合はルームが正本。
 * 端末はルームの WebSocket へつなぎ替えて本番のポーリングを止め、`closed` を受けたら・`room` が無くなったら今のポーリングに戻る。
 * **`room` が無ければ（ルームが開いていない）今のポーリングのまま何も変わらない**（phase は poll のまま、actions は apply だけ）。
 *
 * phase:
 *   poll        ルームが無い。本番を今までどおり読む（返りを apply）
 *   connecting  ルームの WebSocket を開いて hello を送った。最初の state を待っている
 *   open        つながっている。state / progress が来る。書き込みはルームへ（req ごとに ack / error）
 *   retry       切れた（再接続中）。本番の OcgSeatState を間を空けて読み、room があればつなぎ直す（鍵が変わっていてもそのまま使える）・無ければ poll に戻る。
 *               **本番の state は使わない**（ルームが開いているあいだ本番の記録は古いまま）
 *
 * link: { phase, room, rev, attempt, opened, conn, lastRecvMs, lastSentMs, closed }
 *   closed は最後に closed を受けたルームの id（返却を本番が受け取り終えるまで本番の返りに room が残るので、同じルームへは入り直さない）
 *   rev はルームで最後に見た版（hello に付ける。ルームは hello を受けたら最新の state を丸ごと返す）。opened は一度でも state を受けたか（「再接続中」と「接続中」の出し分け）
 *
 * event:
 *   { type: 'seat', body, now }        本番の OcgSeatState の返り（ポーリング・開いたとき）
 *   { type: 'open', conn, now }        WebSocket が開いた（conn は connect の番号。古いつなぎ先からの事象は捨てる）
 *   { type: 'message', msg, conn, now } ルームから 1 通
 *   { type: 'socketClosed', conn, now } WebSocket が閉じた・失敗した
 *   { type: 'tick', now }              殻の時計（数秒ごと）。黙ったつなぎ先を切る・ping を送る
 *   { type: 'request', req, msg, now } 書き込み（answer / register / undo / progress / skip / catchup / policy / close）。req は殻が付ける番号
 *   { type: 'leave' }                  画面を閉じる
 * roomStep(link, event) → { link, actions }
 * actions:
 *   { do: 'connect', url, conn }       WebSocket を開く（url は room.ws_url のまま。相対なら殻が本番の origin で解決＝wsUrl）
 *   { do: 'disconnect' }               WebSocket を閉じる（閉じたあとの socketClosed は無視される）
 *   { do: 'send', msg }                1 通送る（JSON）
 *   { do: 'apply', body }              本番の返りを今までどおり画面へ（poll のときだけ）
 *   { do: 'state', msg }               ルームの state（形は本番の OcgSeatState の席向けの返りと同じ＋ルームの印）。画面へは roomBody で直してから
 *   { do: 'progress', progress }       途中経過だけ
 *   { do: 'resolve', req, result }     書き込みの結果。result は本番の書き込みと同じ { ok, status, body }（body.error / stale / force_required）
 *   { do: 'rejectAll', result }        待っている書き込みを全部この結果で終える（切れた・閉じた）
 *   { do: 'reload' }                   本番から読み直す（ルームが閉じて記録が返った）
 *   { do: 'notice', text }             一言出す
 */

/** つながっているあいだ、何も来なければこの間隔で ping を送る（ルームも 30 秒ごとに WebSocket の ping を送る。CloudFront は 10 分で無通信を切る） */
export const ROOM_PING_MS = 20000;
/** これだけ何も来なければ切れたとみなして（再接続中）つなぎ直す */
export const ROOM_DEAD_MS = 45000;
/** 再接続中に本番の OcgSeatState を読み直す間隔（試行の回数ごと。最後の値で続ける） */
export const ROOM_RETRY_MS = [500, 1000, 2000, 4000, 8000];
/** ack が返らない書き込み（途中経過はルームが progress を全員へ送るだけ） */
const NO_ACK = new Set(['progress']);

export function initialLink() {
  return { phase: 'poll', room: null, rev: null, attempt: 0, opened: false, conn: 0, lastRecvMs: null, lastSentMs: null, closed: null };
}

/** 本番の返り → ルームの入口（無ければ null。room_id・ws_url・key が揃っていないものも null＝今のポーリング） */
export function roomOf(body) {
  const r = body?.room;
  if (!r || typeof r !== 'object' || !r.room_id || !r.ws_url || !r.key) return null;
  return { room_id: String(r.room_id), ws_url: String(r.ws_url), key: String(r.key), seat: r.seat === 0 || r.seat === 1 ? r.seat : null };
}

const sameRoom = (a, b) => !!a && !!b && a.room_id === b.room_id && a.ws_url === b.ws_url && a.key === b.key && a.seat === b.seat;

/** ws_url（`wss://…/room/ws` か `/room/ws` のような相対）→ 開く URL。base は本番の origin（`https://www.ds…`。空なら今のページ） */
export function wsUrl(url, base = '') {
  if (/^wss?:\/\//.test(url)) return url;
  if (/^https?:\/\//.test(url)) return url.replace(/^http/, 'ws');
  const origin = String(base || '').replace(/\/+$/, '');
  if (!origin) return url;
  return `${origin.replace(/^http/, 'ws')}${url.startsWith('/') ? '' : '/'}${url}`;
}

/** 最初の 1 通（ルームの hello。席なしは観戦） */
export function helloMessage(room, rev = null) {
  return { type: 'hello', room: room.room_id, seat: room.seat, key: room.key, ...(rev != null ? { rev } : {}) };
}

/** 今の問い合わせ・登録への答え（本番の OcgAnswer の seat 付きと同じ中身）。register_key があれば register */
export function answerMessage({ answer, rev = null, videoMs = null, touches = null, force = false, autoPass = null, asAuto = false } = {}) {
  return {
    type: answer?.register_key != null ? 'register' : 'answer', answer,
    ...(rev != null ? { rev } : {}), ...(videoMs != null ? { video_ms: videoMs } : {}),
    ...(Array.isArray(touches) && touches.length ? { touches } : {}),
    ...(force ? { force: true } : {}), ...(autoPass ? { auto_pass: autoPass } : {}), ...(asAuto ? { as_auto: true } : {}),
  };
}

/** 戻す（count が無ければ最後の人の選択まで＝本番の OcgSeatUndo。count は「さっき通したところ」へ戻す数＝本番の OcgUndo の count） */
export function undoMessage({ rev = null, count = null } = {}) {
  return { type: 'undo', ...(rev != null ? { rev } : {}), ...(count != null ? { count } : {}) };
}

/** 選んでいる途中（本番の OcgSeatProgress と同じ） */
export function progressMessage({ queryId, op, index = null, allSeats = false } = {}) {
  return { type: 'progress', query_id: queryId, op, ...(index != null ? { index } : {}), ...(allSeats ? { all_seats: true } : {}) };
}

/** ルームのエラー → 本番の書き込みの失敗と同じ形（画面は body.error / body.stale / body.force_required だけを見る） */
export function errorResult(msg) {
  const kind = msg?.kind ?? 'invalid';
  return {
    ok: false, status: 400, room: true,
    body: { error: msg?.message ?? kind, kind, stale: kind === 'stale', waiting: kind === 'waiting', force_required: !!msg?.force_required,
      ...(msg?.rev != null ? { rev: msg.rev } : {}), ...(msg?.stopped ? { stopped: msg.stopped } : {}) },
  };
}

/** つながっていないときの書き込みの結果 */
export const OFFLINE = Object.freeze({ ok: false, status: 0, room: true, body: { error: 'ルームにつながっていません（再接続中）。つながったらもう一度押してください', kind: 'offline' } });

/**
 * ルームの state → 画面に入れる形（本番の OcgSeatState の席向けの返りと同じ）。見せ終わり待ち（wait）のあいだルームは input を null で送るので、
 * 前の input（prevInput）を残して問い合わせだけ消す（LP・フェイズ・登録の一覧・ticker は演出が終わるまで前のまま。解けたら増えた ticker が流れる）
 */
export function roomBody(msg, prevInput = null) {
  if (!msg) return msg;
  if (msg.input != null || !msg.wait) return msg;
  const input = prevInput ? { ...prevInput, query: null, seat_queries: null } : null;
  return { ...msg, input };
}

/** 見せ終わり待ち（{ answer, since_ms } | null） */
export const waitOf = (msg) => msg?.wait ?? null;

/** 画面に出す状態: { active: ルームを使っているか, phase, label } */
export function roomStatus(link) {
  const l = link ?? initialLink();
  if (l.phase === 'poll') return { active: false, phase: 'poll', label: null };
  if (l.phase === 'open') return { active: true, phase: 'open', label: 'ルーム' };
  if (l.phase === 'connecting' && !l.opened) return { active: true, phase: 'connecting', label: 'ルームにつないでいます…' };
  return { active: true, phase: l.phase, label: '再接続中…' };
}

/** 本番の OcgSeatState を読むか（poll と再接続中だけ。つながっているあいだはポーリングを止める） */
export const needsPoll = (link) => link?.phase === 'poll' || link?.phase === 'retry';

/** 次に本番を読むまでの ms（poll は pollMs、再接続中は回数で延ばす） */
export function pollDelay(link, pollMs) {
  if (link?.phase !== 'retry') return pollMs;
  const i = Math.max(0, Math.min(ROOM_RETRY_MS.length - 1, (link.attempt ?? 1) - 1));
  return ROOM_RETRY_MS[i];
}

const connect = (link, room, now) => {
  const conn = (link.conn ?? 0) + 1;
  return { link: { ...link, phase: 'connecting', room, conn, lastRecvMs: now ?? null, lastSentMs: now ?? null }, actions: [{ do: 'connect', url: room.ws_url, conn }] };
};

const lost = (link, now, extra = []) => ({
  link: { ...link, phase: 'retry', attempt: (link.attempt ?? 0) + 1, lastRecvMs: now ?? null },
  actions: [{ do: 'disconnect' }, { do: 'rejectAll', result: OFFLINE }, ...extra],
});

export function roomStep(link, event) {
  const l = link ?? initialLink();
  const e = event ?? {};
  const now = e.now ?? null;
  // 前のつなぎ先から遅れて届いたもの（つなぎ直したあと）は捨てる
  if ((e.type === 'open' || e.type === 'message' || e.type === 'socketClosed') && e.conn != null && e.conn !== l.conn) return { link: l, actions: [] };
  switch (e.type) {
    case 'seat': {
      const room = roomOf(e.body);
      if (l.phase === 'poll') {
        if (room && room.room_id !== l.closed) return connect({ ...l, rev: e.body?.rev ?? l.rev, attempt: 0, opened: false }, room, now);
        return { link: l, actions: [{ do: 'apply', body: e.body }] };
      }
      if (l.phase === 'retry') {
        if (room) return connect(l, room, now);
        // ルームが無くなった（閉じて記録が本番に返った・フラグを OFF にした）→ 今のポーリングへ。版が変わっているので全部読み直す
        return { link: { ...initialLink(), conn: l.conn, closed: l.room?.room_id ?? l.closed }, actions: [{ do: 'notice', text: 'ルームが閉じました。本番の記録に戻ります' }, { do: 'reload' }] };
      }
      // つながっているあいだに届いた本番の返り（開いた直後の読み込みと行き違い）: 別のルームに変わっていればつなぎ直す
      if (room && !sameRoom(room, l.room)) {
        const r = connect(l, room, now);
        return { link: r.link, actions: [{ do: 'disconnect' }, { do: 'rejectAll', result: OFFLINE }, ...r.actions] };
      }
      return { link: l, actions: [] };
    }
    case 'open': {
      if (l.phase !== 'connecting' || !l.room) return { link: l, actions: [] };
      return { link: { ...l, lastSentMs: now, lastRecvMs: now }, actions: [{ do: 'send', msg: helloMessage(l.room, l.rev) }] };
    }
    case 'message': {
      if (l.phase !== 'connecting' && l.phase !== 'open') return { link: l, actions: [] };
      const msg = e.msg ?? {};
      const seen = { ...l, lastRecvMs: now };
      switch (msg.type) {
        case 'state': {
          const back = l.opened && l.phase === 'connecting';
          return { link: { ...seen, phase: 'open', attempt: 0, opened: true, rev: msg.rev ?? l.rev },
            actions: [{ do: 'state', msg }, ...(back ? [{ do: 'notice', text: 'ルームにつなぎ直しました' }] : [])] };
        }
        case 'progress':
          return { link: seen, actions: [{ do: 'progress', progress: msg.progress ?? null }] };
        case 'ack':
          return { link: { ...seen, rev: msg.rev ?? l.rev }, actions: msg.req != null ? [{ do: 'resolve', req: msg.req, result: { ok: true, status: 200, room: true, body: msg } }] : [] };
        case 'error':
          if (msg.req != null) return { link: seen, actions: [{ do: 'resolve', req: msg.req, result: errorResult(msg) }] };
          // 入れなかった（鍵の期限・ルームが無い）: 再接続中にして本番に入口を聞き直す
          return lost(seen, now, [{ do: 'notice', text: `ルームに入れません: ${msg.message ?? msg.kind ?? '?'}` }]);
        case 'closed':
          return { link: { ...initialLink(), conn: l.conn, closed: l.room?.room_id ?? null },
            actions: [{ do: 'disconnect' }, { do: 'rejectAll', result: errorResult({ kind: 'closed', message: 'ルームは閉じました' }) },
              { do: 'notice', text: '対戦を終えました（ルームを閉じて記録を本番へ返しました）' }, { do: 'reload' }] };
        default:
          return { link: seen, actions: [] };   // pong など
      }
    }
    case 'socketClosed':
      if (l.phase !== 'connecting' && l.phase !== 'open') return { link: l, actions: [] };
      return lost(l, now);
    case 'tick': {
      if (l.phase !== 'connecting' && l.phase !== 'open') return { link: l, actions: [] };
      if (now != null && l.lastRecvMs != null && now - l.lastRecvMs > ROOM_DEAD_MS) return lost(l, now);
      if (l.phase === 'open' && now != null && (l.lastSentMs == null || now - l.lastSentMs >= ROOM_PING_MS)) {
        return { link: { ...l, lastSentMs: now }, actions: [{ do: 'send', msg: { type: 'ping' } }] };
      }
      return { link: l, actions: [] };
    }
    case 'request': {
      if (l.phase !== 'open') return { link: l, actions: [{ do: 'resolve', req: e.req, result: OFFLINE }] };
      const msg = { ...(e.msg ?? {}), req: e.req };
      const actions = [{ do: 'send', msg }];
      if (NO_ACK.has(msg.type)) actions.push({ do: 'resolve', req: e.req, result: { ok: true, status: 200, room: true, body: null } });
      return { link: { ...l, lastSentMs: now ?? l.lastSentMs }, actions };
    }
    case 'leave':
      return { link: { ...initialLink(), conn: l.conn, closed: l.closed }, actions: l.phase === 'poll' ? [] : [{ do: 'disconnect' }, { do: 'rejectAll', result: OFFLINE }] };
    default:
      return { link: l, actions: [] };
  }
}
